import React from 'react';
import { Gauge } from 'lucide-react';
import type { LearningEngagement } from '@/services/PagesServices/AnalyticsService';
import { safeNum, type ComputedInsights } from '../utils';

interface HealthScoreCardProps {
  healthScore: number;
  insights: ComputedInsights;
  engagement: LearningEngagement | null;
}

const HealthScoreCard: React.FC<HealthScoreCardProps> = ({ healthScore, insights, engagement }) => {
  const activeRate = engagement
    ? Math.round((safeNum(engagement.activeUsersLast7Days) / Math.max(safeNum(engagement.totalActiveUsers), 1)) * 100)
    : 0;

  return (
    <div className="analytics-section" style={{ marginBottom: 0 }}>
      <div className="analytics-section-title">
        <Gauge size={20} className="analytics-section-icon" />
        Platform Health Score
      </div>
      <div className="analytics-section-subtitle">Composite score based on key platform metrics</div>
      <div className="analytics-health-score-container">
        <div className="analytics-health-gauge">
          <svg viewBox="0 0 120 80" className="analytics-gauge-svg">
            <defs>
              <linearGradient id="gaugeGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#dc2626" />
                <stop offset="33%" stopColor="#f59e0b" />
                <stop offset="66%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
            <path
              d="M 15 70 A 45 45 0 0 1 105 70"
              fill="none"
              stroke="var(--color-border, #e5e7eb)"
              strokeWidth="10"
              strokeLinecap="round"
            />
            <path
              d="M 15 70 A 45 45 0 0 1 105 70"
              fill="none"
              stroke="url(#gaugeGradient)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${(healthScore / 100) * 141.37} 141.37`}
            />
            <text x="60" y="62" textAnchor="middle" fill="var(--color-black-text)" fontSize="24" fontWeight="700">
              {healthScore}
            </text>
            <text x="60" y="76" textAnchor="middle" fill="var(--color-secondary-text)" fontSize="9">
              {healthScore >= 80 ? 'Excellent' : healthScore >= 60 ? 'Good' : healthScore >= 40 ? 'Fair' : 'Needs Attention'}
            </text>
          </svg>
        </div>
        <div className="analytics-health-factors">
          <div className="analytics-health-factor">
            <span>Completion Rate</span>
            <div className="analytics-health-factor-bar">
              <div className="analytics-progress-bar" style={{ height: '6px' }}>
                <div
                  className={`analytics-progress-fill ${insights.avgCompletionRate >= 70 ? 'high' : insights.avgCompletionRate >= 40 ? 'medium' : 'low'}`}
                  style={{ width: `${insights.avgCompletionRate}%` }}
                />
              </div>
              <span className="analytics-health-factor-value">{insights.avgCompletionRate}%</span>
            </div>
          </div>
          <div className="analytics-health-factor">
            <span>Enrollment Rate</span>
            <div className="analytics-health-factor-bar">
              <div className="analytics-progress-bar" style={{ height: '6px' }}>
                <div
                  className={`analytics-progress-fill ${insights.enrollmentRate >= 70 ? 'high' : insights.enrollmentRate >= 40 ? 'medium' : 'low'}`}
                  style={{ width: `${insights.enrollmentRate}%` }}
                />
              </div>
              <span className="analytics-health-factor-value">{insights.enrollmentRate}%</span>
            </div>
          </div>
          <div className="analytics-health-factor">
            <span>7-Day Active Rate</span>
            <div className="analytics-health-factor-bar">
              <div className="analytics-progress-bar" style={{ height: '6px' }}>
                <div
                  className={`analytics-progress-fill ${activeRate >= 70 ? 'high' : activeRate >= 40 ? 'medium' : 'low'}`}
                  style={{ width: `${activeRate}%` }}
                />
              </div>
              <span className="analytics-health-factor-value">{activeRate}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(HealthScoreCard);
